import React, { useEffect, useState } from 'react';
import { BID_INCREMENTS } from '../domain/constants';
import { isRoleFull, maxBid } from '../domain/team';

// Il giocatore in asta, con i comandi per rilanciare.
//
// Sostituisce le due versioni precedenti — AuctionPanel per il desktop e
// MobileAuctionPanel per il telefono — che erano alberi separati scelti da una
// media query. Da quella separazione nascevano difetti di una parte sola: la
// versione mobile, per esempio, non mostrava mai il tetto di spesa. Qui il
// componente e uno e cambia forma in base alla larghezza del proprio
// contenitore, non della finestra: la scheda vive dentro uno split che l'utente
// trascina, quindi la finestra e la misura sbagliata.
//
// I controlli sui bottoni sono un aiuto, non la regola: il limite di ruolo e il
// tetto di spesa li verifica comunque il server.

export default function LotCard({
    lot,
    remainingSeconds,
    auctionTimer,
    participants,
    team,
    playersPerTeam,
    participantId,
    catalogue,
    onBid,
    onProxyBid,
    hasProxyBid,
}) {
    const [proxyValue, setProxyValue] = useState('');

    useEffect(() => {
        setProxyValue('');
    }, [lot?.id]);

    if (!lot) return null;

    const player = catalogue[lot.player_id];
    const leader = participants.find((participant) => participant.id === lot.leading_participant_id);
    const isLeading = lot.leading_participant_id === participantId;
    const roleFull = isRoleFull(team, lot.role, playersPerTeam);
    const ceiling = maxBid(team, playersPerTeam);

    // Un solo numero (0-100) guida sia la larghezza del filetto sia il suo
    // colore: il CSS lo legge da --clock. Senza timer configurato la barra
    // resta piena invece di sparire.
    const clock = auctionTimer
        ? Math.max(0, Math.min(100, (remainingSeconds / auctionTimer) * 100))
        : 100;

    const fullName = player?.['Nome Completo'] ?? lot.player_name;
    const surname = player?.Cognome || fullName;
    const club = player?.Squadra;

    const disabledReason = roleFull
        ? 'Hai gia raggiunto il numero massimo di giocatori per questo ruolo.'
        : isLeading
          ? 'Sei gia in testa.'
          : null;

    const bidDisabled = (amount) =>
        Boolean(disabledReason) || lot.current_bid + amount > ceiling;

    const proxyAmount = Number.parseInt(proxyValue, 10);

    return (
        <>
            <section className="lot" style={{ '--clock': clock }} aria-label="Giocatore in asta">
                <div className="lot-clock">
                    <div
                        className="lot-clock-bar"
                        role="progressbar"
                        aria-label="Tempo rimanente"
                        aria-valuenow={remainingSeconds}
                        aria-valuemin={0}
                        aria-valuemax={auctionTimer}
                        aria-valuetext={`${remainingSeconds} secondi`}
                    />
                </div>

                <div className="lot-eyebrow">
                    <span className="u-caps">In asta</span>
                    <span className="lot-seconds">{remainingSeconds}s</span>
                </div>

                <h2 className="lot-name">{surname}</h2>
                <p className="lot-meta">
                    <span className="badge badge--role" data-role={lot.role}>
                        {lot.role}
                    </span>
                    {club && <span>{club}</span>}
                    {/* Il nome di battesimo solo se aggiunge qualcosa: due
                        giocatori con lo stesso cognome sono la norma. */}
                    {fullName !== surname && <span className="u-dim">{fullName}</span>}
                </p>

                <div className="lot-bid">
                    <span className="lot-bid-value">{lot.current_bid}</span>
                    <span className={`lot-leader${isLeading ? ' is-me' : ''}`}>
                        <span className="u-caps">In testa</span>
                        <span className="lot-leader-name">
                            {isLeading ? 'Tu' : (leader?.name ?? '—')}
                        </span>
                    </span>
                    <span className="lot-ceiling">
                        <span className="u-caps">Puoi arrivare a</span>
                        <span className="lot-ceiling-value">{ceiling}</span>
                    </span>
                </div>

                {disabledReason && <p className="lot-note lot-note--stop">{disabledReason}</p>}

                <div className="lot-proxy">
                    <div className="field">
                        <label className="field-label" htmlFor="proxy-bid">
                            Offerta massima automatica {hasProxyBid && '(attiva)'}
                        </label>
                        <div className="field-row">
                            <input
                                id="proxy-bid"
                                type="number"
                                min="1"
                                max={ceiling}
                                className="input"
                                placeholder={`fino a ${ceiling}`}
                                value={proxyValue}
                                onChange={(event) => setProxyValue(event.target.value)}
                                disabled={roleFull}
                            />
                            <button
                                type="button"
                                className="btn btn--ghost"
                                disabled={roleFull || !Number.isInteger(proxyAmount)}
                                onClick={() => onProxyBid(proxyAmount)}
                            >
                                Imposta
                            </button>
                            {hasProxyBid && (
                                <button
                                    type="button"
                                    className="btn btn--quiet"
                                    onClick={() => onProxyBid(null)}
                                >
                                    Togli
                                </button>
                            )}
                        </div>
                        <p className="field-hint">
                            Il server rilancia per te di 1 finche non supera il tuo tetto.
                        </p>
                    </div>
                </div>
            </section>

            {/* I tre incrementi stanno in una barra propria: su telefono e
                ancorata in fondo allo schermo, cosi restano sotto il pollice
                senza dover scorrere proprio nei secondi che contano. */}
            <div className="bid-bar">
                {BID_INCREMENTS.map((amount) => (
                    <button
                        key={amount}
                        type="button"
                        className="btn btn--go"
                        onClick={() => onBid(amount)}
                        disabled={bidDisabled(amount)}
                    >
                        +{amount}
                    </button>
                ))}
            </div>
        </>
    );
}
